// Pure helpers for the client-only features of design v6 (design/inventory-v6.md F1, F6, F10, F12, F13).
// Ported from the reference view-model (design/unpacked/task-panes-v6/logic.split.js, line refs below) and kept
// free of Vue/DOM so they are unit-tested directly (frontend/tests/v6.test.ts). Inputs are the local projection
// (IndexedDB → in-memory projects) already filtered to what the user may see; nothing here fetches data.
import type { Project, Task, TaskList } from '@/domain/model';
import { createdAt } from './constants';

const DAY = 864e5;

// ── F1 Search (L1319–1346) ───────────────────────────────────────────────────────────────────────────────────
export interface TaskHit { p: Project; l: TaskList; t: Task; rank: number }
export interface SearchResult { q: string; projHits: Project[]; taskHits: TaskHit[] }

/** Substring search over project names and task title + notes + checklist text. `null` for an empty query.
 *  Ranking (stable): open title hits, open body hits, done title hits, done body hits. */
export function searchLocal(visible: Project[], query: string): SearchResult | null {
  const q = (query || '').trim().toLowerCase();
  if (!q) return null;
  const taskHits: TaskHit[] = [];
  for (const p of visible) for (const l of p.lists) for (const t of l.tasks) {
    const hay = [t.title, t.notes, ...(t.checklist || []).map(c => c.text)].filter(Boolean).join(' ').toLowerCase();
    if (hay.includes(q)) taskHits.push({ p, l, t, rank: (t.done ? 2 : 0) + ((t.title || '').toLowerCase().includes(q) ? 0 : 1) });
  }
  taskHits.sort((a, b) => a.rank - b.rank);
  return { q, projHits: visible.filter(p => p.name.toLowerCase().includes(q)), taskHits };
}

// ── F6 Background chart: tasks added / done per day, last 30 days (L1703–1724) ──────────────────────────────
export const BG_DAYS = 30;
export interface BgStatData {
  start: number; done: number[]; added: number[]; total: number; totalA: number;
  pts: Array<[number, number]>; ptsA: Array<[number, number]>; line: string; lineA: string; grid: number[];
}
const midnight = (ms: number) => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };
/** Local-calendar day index of `ms` counted from `start` (a local midnight); DST-safe (days may be 23/25 h). */
export function dayIndex(start: number, ms: number): number { return Math.round((midnight(ms) - start) / DAY); }
/** When the task was last completed (its last "completed" log entry), or null. */
export const completedAt = (t: Task): number | null => ((t.log || []).filter(e => e.k === 'done' && e.text === 'completed').pop() || null)?.at ?? null;

/** Added = tasks created in the window (still present, i.e. not in Trash); Done = tasks completed in the window
 *  and still completed, counted on their last completion day. Raw counts; curves use 0.25/0.5/0.25 smoothing. */
export function bgStatData(visible: Project[], now: number = Date.now()): BgStatData {
  const t0 = midnight(now);
  const startD = new Date(t0); startD.setDate(startD.getDate() - (BG_DAYS - 1));
  const start = startD.getTime();
  const done = new Array<number>(BG_DAYS).fill(0), added = new Array<number>(BG_DAYS).fill(0);
  for (const p of visible) for (const l of p.lists) for (const t of l.tasks) {
    const ca = createdAt(t) || 0;
    if (ca >= start) added[Math.min(BG_DAYS - 1, dayIndex(start, ca))]!++;
    if (!t.done) continue;
    const at = completedAt(t) || 0;
    if (at >= start) done[Math.min(BG_DAYS - 1, dayIndex(start, at))]!++;
  }
  const smooth = (a: number[]) => a.map((_, i) => (a[i - 1] ?? a[i]!) * 0.25 + a[i]! * 0.5 + (a[i + 1] ?? a[i]!) * 0.25);
  const sm = smooth(done), sa = smooth(added), mx = Math.max(1, ...sm, ...sa);
  const toPts = (arr: number[]) => arr.map((v, i) => [(i / (BG_DAYS - 1)) * 300, 100 - (v / mx) * 88 - 4] as [number, number]);
  const toLine = (p: Array<[number, number]>) => { let d = 'M' + p[0]![0] + ',' + p[0]![1].toFixed(1); for (let i = 1; i < p.length; i++) { const [x0, y0] = p[i - 1]!, [x1, y1] = p[i]!, cx = (x0 + x1) / 2; d += ' C' + cx.toFixed(1) + ',' + y0.toFixed(1) + ' ' + cx.toFixed(1) + ',' + y1.toFixed(1) + ' ' + x1.toFixed(1) + ',' + y1.toFixed(1); } return d; };
  const pts = toPts(sm), ptsA = toPts(sa);
  return { start, done, added, total: done.reduce((a, x) => a + x, 0), totalA: added.reduce((a, x) => a + x, 0),
    pts, ptsA, line: toLine(pts), lineA: toLine(ptsA), grid: [0.25, 0.5, 0.75].map(f => 100 - f * 88 - 4) };
}

// ── F10 Personal project order (ADR 0011 decision 5) ─────────────────────────────────────────────────────────
/** Projects in the user's saved order; projects missing from it keep their shared order after the ordered ones. */
export function applyOrder<T extends { id: string }>(projects: T[], order: string[] | undefined): T[] {
  if (!order || !order.length) return projects;
  const pos = new Map(order.map((id, i) => [id, i]));
  const known = projects.filter(p => pos.has(p.id)).sort((a, b) => pos.get(a.id)! - pos.get(b.id)!);
  return [...known, ...projects.filter(p => !pos.has(p.id))];
}
/** Moves `id` before/after `target` in `ids` (reference drop rule, L1352–1354). Returns the same array if no-op. */
export function moveInOrder(ids: string[], id: string, target: string, after: boolean): string[] {
  if (id === target || !ids.includes(id) || !ids.includes(target)) return ids;
  const out = ids.filter(x => x !== id);
  out.splice(out.indexOf(target) + (after ? 1 : 0), 0, id);
  return out.every((x, i) => x === ids[i]) ? ids : out;
}

// ── F12 All Tasks "By list" (L1487–1495) ─────────────────────────────────────────────────────────────────────
/** Grouping key of a list title: trimmed, lower-cased; Arabic yeh/kaf folded to Persian and ZWNJ ignored so the
 *  same Persian list name typed on different keyboards merges (production addition, inventory F12 risk). */
export const listKey = (title: string | undefined): string => (title || '').trim().normalize('NFC').toLowerCase().replace(/ي/g, 'ی').replace(/ك/g, 'ک').replace(/‌/g, '');
export interface ListRow { p: Project; l: TaskList; t: Task; g?: number; gl?: string; pi?: number; ti?: number }
/** Sorts rows by list group (first appearance scanning list index 0,1,2… across projects in order), then project
 *  order, then position in the list; sets `g` (group index) and `gl` (group label = first title seen). */
export function groupByList<R extends ListRow>(visible: Project[], rows: R[]): Array<R & { g: number; gl: string }> {
  const groups = new Map<string, string>(), maxL = Math.max(0, ...visible.map(p => p.lists.length));
  for (let i = 0; i < maxL; i++) for (const p of visible) { const l = p.lists[i]; if (!l) continue; const k = listKey(l.title); if (!groups.has(k)) groups.set(k, (l.title || '').trim()); }
  const gi = [...groups.keys()];
  for (const r of rows) { r.g = gi.indexOf(listKey(r.l.title)); r.pi = visible.indexOf(r.p); r.ti = r.l.tasks.indexOf(r.t); }
  rows.sort((x, y) => x.g! - y.g! || x.pi! - y.pi! || x.ti! - y.ti!);
  for (const r of rows) r.gl = groups.get(gi[r.g!]!) ?? '';
  return rows as Array<R & { g: number; gl: string }>;
}

// ── F13 "#" in task notes picks a tag (L346–368) ─────────────────────────────────────────────────────────────
export interface Trigger { kind: '@' | '#'; q: string; start: number; end: number }
/** Mention/tag trigger before the caret: `@q` or `#q` at the start or after whitespace. */
export function scanTrigger(value: string, caret: number): Trigger | null {
  const m = value.slice(0, caret).match(/(^|\s)([@#])([^\s@#]*)$/);
  if (!m) return null;
  return { kind: m[2] as '@' | '#', q: m[3]!, start: caret - m[3]!.length - 1, end: caret };
}
export interface TagOption { tag: string; isNew?: boolean }
/** Project tags (tag order without the "No tag" bucket, plus tags used on tasks) matching the prefix (spaces
 *  ignored), max 6; plus a "new tag" option when the typed text is not an existing tag. */
export function hashOptions(proj: Project, q: string): TagOption[] {
  const ql = q.toLowerCase();
  const all = [...new Set([...(proj.tagOrder || []).filter(g => g !== '__none'), ...proj.lists.flatMap(l => l.tasks.flatMap(t => t.tags))])];
  const hits: TagOption[] = all.filter(g => g.toLowerCase().replace(/\s+/g, '').startsWith(ql)).slice(0, 6).map(g => ({ tag: g }));
  if (q && !all.some(g => g.toLowerCase() === ql)) hits.push({ tag: q, isNew: true });
  return hits;
}
/** Replaces the `#query` with `#tag ` (spaces removed) and returns the new text and caret. */
export function insertHashTag(value: string, trig: Pick<Trigger, 'start' | 'end'>, tag: string): { value: string; caret: number } {
  const ins = '#' + tag.replace(/\s+/g, '') + ' ';
  return { value: value.slice(0, trig.start) + ins + value.slice(trig.end), caret: trig.start + ins.length };
}
